import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTimer } from 'react-timer-hook'
import { useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import { Button, Input, PinCode, Toast, useInput, usePinCode, useViewport } from '@chilecompra/react-kit/components'
import {
  checkInputErrorEmail,
  checkInputErrorMaxLength,
  checkInputErrorRequired,
  EMAIL_CHECK
} from '@chilecompra/react-kit/utils'
import { Spinner, useEffectOnce } from '@chilecompra/react-kit'
import {
  onRegisterBy,
  onRegisterCodeValidationThunk,
  onResetRegisterWizardState,
  registerFlowThunk,
  sendValidationEmailThunk
} from '../RegisterWizard.actions'
import {
  DesktopNavigation,
  Hidden,
  Image,
  ImageContainer,
  Instructions,
  MobileButtons,
  MobileNavigation,
  Root,
  Section,
  Label,
  TimerLabelContainer,
  Title,
  ToastContainer,
  SectionInputContainer,
  SectionPincodeContainer
} from './RegisterWizardEmailVerify.styles'

/**
 * The RegisterWizardEmailVerify' container.
 */
const RegisterWizardEmailVerify = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { size } = useViewport()
  const pinCodeRef = useRef(null)
  const { emailSent, errorSendingEmail, codeIsValid, sendingEmail } = useSelector(
    state => state.registerWizard.emailVerify
  )
  const { error, loading, guest } = useSelector(state => state.registerWizard)
  const flow = 'INICIO_PASO_2'

  const { seconds, minutes, isRunning, restart } = useTimer({ expiryTimestamp: new Date() })
  const {
    error: emailError,
    value: emailValue,
    onChange: handleEmailChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired('Debes ingresar tu dirección de correo.'),
      checkInputErrorEmail('Tu dirección de correo parece estar en un formato incorrecto.'),
      checkInputErrorMaxLength(80, 'Tu dirección de correo excede el máximo de 80 carácteres permitidos.')
    ],
    initialValue: ''
  })
  const { value: codeValue, onChange: handleCodeChange } = usePinCode()
  const remainingTime = useMemo(() => {
    return `${minutes} ${minutes > 1 ? 'minutos' : 'minuto'}  y ${seconds >= 10 ? seconds.toString()[0] : '0'}${
      seconds >= 10 ? seconds.toString()[1] : seconds.toString()
    } segundos`
  }, [minutes, seconds])

  const confTimer = () => {
    const time = new Date()
    time.setMinutes(time.getMinutes() + 3)
    restart(time)
  }

  const handleSendingCode = () => {
    const lastNameParts = guest.lastName.split(' ')
    const fatherLastName = lastNameParts[0]
    const motherLastName = lastNameParts.reduce(
      (acc, lastname, key) => (key === 0 ? '' : key === 1 ? lastname : `${acc} ${lastname}`),
      ''
    )

    const data = {
      email: emailValue,
      dni: guest.dni,
      name: guest.name,
      motherLastName,
      fatherLastName,
      callback: () => confTimer()
    }

    const mockedDataForTestPurposes = {
      email: emailValue,
      dni: '333.333.333-3',
      name: 'Marianela Andrea',
      motherLastName: 'Jiménez',
      fatherLastName: 'Iturriaga',
      callback: () => confTimer()
    }

    if (!guest) {
      dispatch(sendValidationEmailThunk(mockedDataForTestPurposes))
    }

    dispatch(sendValidationEmailThunk(data))
  }

  const handleBack = () => {
    dispatch(onResetRegisterWizardState())
    onChangeStep('welcome')
  }

  const handleRegisterByUniquePass = () => {
    dispatch(onRegisterBy('business-search'))
    onChangeStep('business-search')
  }

  const canNotNextButtonClick = useCallback(() => {
    const hadErrors = emailError !== ''
    const hadInvalidValues = emailValue === ''
    const hadInvalidCodeValues = codeValue.some(value => value === '')

    return hadErrors || hadInvalidValues || hadInvalidCodeValues || loading || !codeIsValid
  }, [codeValue, emailValue, loading, emailError, codeIsValid])

  const canNotSendingCode = useCallback(() => {
    const hadErrors = emailError !== ''
    const hadInvalidValues = emailValue === ''
    const hadErrorEmail = !EMAIL_CHECK.test(emailValue)

    return hadErrors || hadInvalidValues || hadErrorEmail || isRunning || sendingEmail
  }, [emailError, emailValue, isRunning, sendingEmail])

  const disabledNextButton = canNotNextButtonClick()
  const disabledSendingCode = canNotSendingCode()

  const renderToast = () => {
    return (
      <ToastContainer role="alert" aria-live="assertive">
        <Toast
          tabIndex="0"
          disableIcon
          margin={size.isTiny ? '20px 0 0' : size.isDownToLarge ? '18px 0 0' : '53px 0 0'}
          severity="default"
          width={size.isTiny ? '90%' : size.isDownToLarge ? '100%' : '263px'}
        >
          Recuerda registrar una dirección de correo electrónico laboral a la cuál tengas acceso.
        </Toast>
      </ToastContainer>
    )
  }

  /**
   * Validates the user's input to ensure that a complete code (at least 4 digits) has been entered.
   * Once the code is fully entered, it dispatches the `onRegisterCodeValidationThunk` action to validate the code against the backend. */

  useEffect(() => {
    const isCodeComplete = codeValue.every(char => char !== '')
    if (isCodeComplete) {
      dispatch(onRegisterCodeValidationThunk({ email: emailValue, code: codeValue.join('') }))
      if (pinCodeRef.current) {
        pinCodeRef.current.blur()
      }
    }
  }, [codeValue, dispatch, emailValue])

  useEffectOnce(() => {
    dispatch(registerFlowThunk({ flow }))
  }, [dispatch])

  useAnalyticsPageView('/paso/2/correo')

  return (
    <Root>
      <Section container>
        <Section item lg={7} xs={12}>
          <Title tabIndex="0" aria-label="Paso 2: Ingresa tu correo electrónico">
            Ingresa tu correo electrónico
          </Title>
          <Instructions tabIndex="0">
            Enviaremos un código de verificación a la dirección de correo electrónico que ingreses a continuación.
          </Instructions>
          {size.isTiny && renderToast()}
          {error && !loading && errorSendingEmail && (
            <ToastContainer>
              <Toast tabIndex="0" margin="20px 0" severity="warning" width="90%">
                En estos momentos no podemos enviarte el código, por favor espera unos minutos e intenta de nuevo.
              </Toast>
            </ToastContainer>
          )}

          <Section container margin={size.isTiny ? '21px 0 0' : '32px 0 0'}>
            <SectionInputContainer
              width="100%"
              display="flex"
              flexDirection={size.isTiny || size.isDownToLarge ? 'column' : 'row'}
              alignItems="center"
              gap="20px"
            >
              <Input
                autoFocus
                error={emailError}
                label="Correo electrónico laboral"
                aria-label="Correo electrónico laboral"
                placeholder="email@ejemplo.com"
                value={emailValue}
                onChange={handleEmailChange}
              />

              <Button
                color="default"
                disabled={disabledSendingCode || codeIsValid}
                loading={sendingEmail}
                variant={!emailSent || !isRunning ? 'contained' : 'outlined'}
                onClick={handleSendingCode}
                width={size.isTiny ? '90%' : size.isDownToLarge ? '100%' : '42%'}
                margin={size.isTiny ? '-25px 0 10px 0' : size.isDownToLarge ? '-25px 0 0' : ''}
                tabIndex="0"
                aria-label={!emailSent ? 'Enviar código' : !isRunning ? 'Reenviar código' : 'Código enviado'}
              >
                {!emailSent ? 'Enviar código' : !isRunning && !codeIsValid ? 'Reenviar código' : '¡Código enviado!'}
              </Button>
            </SectionInputContainer>
          </Section>
          {emailSent && !codeIsValid && isRunning && (
            <Section
              container
              margin={size.isUpToMedium ? '0 0 15px' : '14px 0 0'}
              padding="4px"
              spacing={1}
              maxWidth="263px"
            >
              <TimerLabelContainer>
                <Label tabIndex="0">{`Quedan ${remainingTime} para ingresar el código que enviamos a tu email.`}</Label>
              </TimerLabelContainer>
            </Section>
          )}
          {emailSent && (
            <Section container padding="4px" margin="14px 0" spacing={1}>
              <SectionPincodeContainer disabled={codeIsValid}>
                <PinCode
                  label="Código de verificación"
                  aria-label="Código de verificación"
                  fieldsLength={4}
                  value={codeValue}
                  onChange={handleCodeChange}
                  autoFocus
                  ref={pinCodeRef}
                  cursor="center"
                />
              </SectionPincodeContainer>
            </Section>
          )}

          {error && codeIsValid !== undefined && codeIsValid === false && (
            <ToastContainer role="alert" aria-live="assertive">
              <Toast
                tabIndex="0"
                display="flex"
                alignItems="center"
                justifyContent="center"
                severity="error"
                margin={size.isTiny ? '-20px 0 10px 0' : size.isDownToLarge ? '' : ''}
                padding="10px 18px"
                width={size.isTiny ? '90%' : size.isDownToLarge ? '91%' : '94%'}
              >
                El código de verificación es incorrecto, por favor ingrésalo de nuevo.
              </Toast>
            </ToastContainer>
          )}

          {!error && codeIsValid !== undefined && codeIsValid && (
            <ToastContainer role="alert" aria-live="assertive">
              <Toast
                tabIndex="0"
                display="flex"
                alignItems="center"
                justifyContent="center"
                severity="success"
                margin={size.isTiny ? '-20px 0 20px 0' : size.isDownToLarge ? '' : '0 0 13px 0'}
                padding="10px 18px"
                width={size.isTiny ? '90%' : size.isDownToLarge ? '91%' : '94%'}
              >
                Has ingresado el código correctamente
              </Toast>
            </ToastContainer>
          )}

          {loading && (
            <Section
              role="status"
              aria-label="Cargando..."
              display="flex"
              justifyContent="center"
              margin={size.isTiny ? '20px 0 20px 0' : '30px 30px'}
            >
              <Spinner />
            </Section>
          )}

          {!size.isTiny && size.isDownToLarge && renderToast()}

          {!size.isTiny && (size.isDownToLarge || size.isUpToLarge) && (
            <Section container margin="33px 0 0">
              <DesktopNavigation>
                <Button tabIndex="0" aria-label="Cancelar" onClick={handleBack} type="button" variant="outlined">
                  Cancelar
                </Button>
                <Button
                  tabIndex="0"
                  aria-label="Siguiente"
                  color="primary"
                  disabled={disabledNextButton}
                  margin={!size.isDownToLarge ? '0 0 0 60px' : ''}
                  type="button"
                  onClick={handleRegisterByUniquePass}
                >
                  Siguiente
                </Button>
              </DesktopNavigation>
            </Section>
          )}
          {size.isTiny && (
            <Section container margin={emailSent ? '20px 0 30px 0' : '58px 0 0 0'}>
              <MobileNavigation>
                <MobileButtons tabIndex="0" aria-label="Cancelar" onClick={handleBack} type="button" variant="outlined">
                  Cancelar
                </MobileButtons>
                <MobileButtons
                  tabIndex="0"
                  aria-label="Siguiente"
                  color="primary"
                  disabled={disabledNextButton}
                  type="button"
                  onClick={handleRegisterByUniquePass}
                >
                  Siguiente
                </MobileButtons>
              </MobileNavigation>
            </Section>
          )}
        </Section>

        <Hidden lgDown>
          <ImageContainer>
            <Image alt="¡Continuemos!" src="/assets/img/steps/paso3.svg" />
            {renderToast()}
          </ImageContainer>
        </Hidden>
      </Section>
    </Root>
  )
}

RegisterWizardEmailVerify.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardEmailVerify.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardEmailVerify
